/*
================================================================================
FILE HEADER — apps/studio-web/src/lib/db.ts
================================================================================

CHANGE LOG
----------
| #  | Date & Time         | Who               | Why                       | What Changed                          |
|----|---------------------|-------------------|---------------------------|---------------------------------------|
| 1  | 2026-06-25 00:00    | Claude Sonnet 4.6 | Test Library (Phase 3)    | Created Dexie DB for Suite+TestCase   |
| 2  | 2026-06-25 00:00    | Claude Sonnet 4.6 | Execution History         | Added DBRun + runs table (v2)         |
| 3  | 2026-07-02 00:00    | Claude Sonnet 4.6 | Reports page overhaul     | Added DBRunGroup, groups table (v3);  |
|    |                     |                   |                           | added browser/groupId/healCount to    |
|    |                     |                   |                           | DBRun; tags/passedSteps/healCount to  |
|    |                     |                   |                           | DBRunTest; coords/hint/screenshot to  |
|    |                     |                   |                           | DBRunStep                             |
| 4  | 2026-07-02 00:00    | Claude Sonnet 4.6 | Functions tab (Phase 3)   | Added DBFunctionFolder, DBFunctionParam|
|    |                     |                   |                           | DBFunction; functionFolders + functions|
|    |                     |                   |                           | tables (v4)                           |

FILES THAT CALL / IMPORT THIS FILE
------------------------------------
- apps/studio-web/src/components/SaveTestModal.tsx
- apps/studio-web/src/components/TestLibrary.tsx
- apps/studio-web/src/components/ReportsPage.tsx
- apps/studio-web/src/components/FunctionsPage.tsx
- apps/studio-web/src/App.tsx

FUNCTIONS / EXPORTS COVERED
----------------------------
- DBSuite           — suite/folder record
- DBTest            — saved test case record
- DBRunGroup        — named group for organizing runs
- DBRunStep         — per-step result inside a run (with coords, hint, screenshot)
- DBRunTest         — per-test result inside a run (with tags, passedSteps, healCount)
- DBRun             — one complete execution run record (with browser, groupId, healCount)
- DBFunctionFolder  — folder for organizing functions
- DBFunctionParam   — named parameter for a function (used via ${paramName} in steps)
- DBFunction        — reusable step sequence (Function Library)
- db                — singleton PlexDatabase instance

CALLER → CALLEE MAP
--------------------
- App.tsx → db.runs.add()
- ReportsPage.tsx → db.runs (useLiveQuery), db.groups (useLiveQuery)
- TestLibrary.tsx → db.tests (CRUD)
- FunctionsPage.tsx → db.functions (CRUD), db.functionFolders (CRUD)

IMPORTANT POINTS
----------------
1. irJson stores the serialized TestIR (JSON string). Kept as string so
   IndexedDB stores it efficiently without structural clone overhead.
2. stepCount is denormalized for fast display without parsing irJson.
3. DBRun.tests stores step-level results as embedded array. Runs are small
   enough that a separate steps table adds unnecessary complexity.
4. DB version 3 adds the groups table and extends DBRun with groupId/browser.
   Dexie auto-migrates from v1/v2/v3 to v4 — existing data is preserved.
5. coords is stored as a formatted string "(x, y)" for direct display.
6. screenshotDataUrl is a base64 PNG. Only set when per-step screenshots
   are captured (extension relay mode). Absent = "—" in the UI.
7. DBFunction.irJson stores IRStep[] (NOT a full TestIR) — functions are
   reusable step sequences without their own flow/config wrapper.

ISSUES FACED / KNOWN GOTCHAS
-----------------------------
- matchedBy, coords, hint, screenshotDataUrl are populated by the runner relay
  in a future pass. Existing runs will show "—" for these columns.
================================================================================
*/

import Dexie, { type Table } from 'dexie';

export interface DBSuite {
  id: string;
  name: string;
  parentId?: string;
  createdAt: number;
  updatedAt: number;
}

export interface DBTest {
  id: string;
  suiteId: string;
  name: string;
  tags: string[];
  irJson: string;
  stepCount: number;
  createdAt: number;
  updatedAt: number;
  lastRunAt?: number;
  lastRunStatus?: 'passed' | 'failed' | 'passed_healed' | 'error';
}

export interface DBRunGroup {
  id: string;
  name: string;
  createdAt: number;
}

export interface DBRunStep {
  stepId: string;
  stepType: string;
  label?: string;
  status: 'ok' | 'not_found' | 'error' | 'skipped';
  durationMs: number;
  matchedBy?: string;
  coords?: string;            // e.g. "(972, 2176)" — document-coordinate tap point
  hint?: string;              // detail/hint text shown in Reports DETAIL/HINT column
  screenshotDataUrl?: string; // base64 PNG thumbnail captured after this step
}

export interface DBRunTest {
  testId: string;
  testName: string;
  status: 'passed' | 'failed';
  steps: DBRunStep[];
  startedAt: number;
  finishedAt: number;
  tags?: string[];        // copied from DBTest.tags at run time
  passedSteps?: number;   // count of steps with status === 'ok'
  healCount?: number;     // count of steps healed by Tier-1 ONNX
}

export interface DBRun {
  id: string;
  runAt: number;
  status: 'passed' | 'failed';
  tests: DBRunTest[];
  totalDurationMs: number;
  passedCount: number;
  failedCount: number;
  browser?: string;    // 'chromium' | 'firefox' | 'webkit'
  groupId?: string;    // references DBRunGroup.id
  healCount?: number;  // total heals across all tests in this run
}

export interface DBFunctionFolder {
  id: string;
  name: string;
  parentId?: string;  // undefined = top-level folder
  createdAt: number;
}

export interface DBFunctionParam {
  name: string;
  description?: string;
  defaultValue?: string;
}

export interface DBFunction {
  id: string;
  name: string;
  folderId?: string;         // references DBFunctionFolder.id; undefined = no folder
  description?: string;
  parameters: DBFunctionParam[];
  irJson: string;            // JSON.stringify(IRStep[]) — step sequence only, no TestIR wrapper
  stepCount: number;         // denormalized for sidebar display
  createdAt: number;
  updatedAt: number;
}

class PlexDatabase extends Dexie {
  suites!:          Table<DBSuite>;
  tests!:           Table<DBTest>;
  runs!:            Table<DBRun>;
  groups!:          Table<DBRunGroup>;
  functionFolders!: Table<DBFunctionFolder>;
  functions!:       Table<DBFunction>;

  constructor() {
    super('plex-studio');
    this.version(1).stores({
      suites: 'id, parentId, createdAt',
      tests:  'id, suiteId, name, createdAt, lastRunStatus',
    });
    this.version(2).stores({
      suites: 'id, parentId, createdAt',
      tests:  'id, suiteId, name, createdAt, lastRunStatus',
      runs:   'id, runAt, status',
    });
    this.version(3).stores({
      suites:          'id, parentId, createdAt',
      tests:           'id, suiteId, name, createdAt, lastRunStatus',
      runs:            'id, runAt, status, groupId',
      groups:          'id, createdAt',
    });
    this.version(4).stores({
      suites:          'id, parentId, createdAt',
      tests:           'id, suiteId, name, createdAt, lastRunStatus',
      runs:            'id, runAt, status, groupId',
      groups:          'id, createdAt',
      functionFolders: 'id, parentId, createdAt',
      functions:       'id, folderId, name, createdAt',
    });
  }
}

export const db = new PlexDatabase();
